import {AccountSwitcherItem, BundleDetails, ChargeOffType, CreditReportDetails} from "../consumer/declarations";
import {getDaysUntilCreditReport} from "../consumer/helpers";
import {ActivePaymentPlan} from "../declarations";
import {ChipType, Colors} from "../ui";
import {orderBy} from "lodash";

interface IGetAccountCardLabel {
  inDispute: boolean;
  inVerificationRequest: boolean;
  creditReportDetails?: CreditReportDetails;
  represented?: boolean;
  bankruptcy?: boolean;
  paymentPlan?: ActivePaymentPlan;
  isCreditRecalled?: boolean;
  isClosed?: boolean;
  isReturned?: boolean;
  isAgent?: boolean;
  accountHasMainPlan?: boolean;
  hasRewardsProgram?: boolean;
  isPaid?: boolean;
}

type AccountCardLabelText =
  | "Outstanding balance"
  | "Verification requested"
  | "Dispute open"
  | "Closed"
  | "Recalled by creditor"
  | "Returned to creditor"
  | "Credit report risk"
  | "Bankruptcy"
  | "Represented"
  | "plan canceled"
  | "No plan yet"
  | "Payment failed"
  | "Resolved"
  | "Temporary plan"
  | "Active plan";

interface AccountCardLabel {
  label: AccountCardLabelText;
  variant: ChipType["variant_"];
  action?: "manage" | "resolve";
  progressBarColor?: string;
}

export const getAccountCardLabel = ({
  inDispute,
  inVerificationRequest,
  creditReportDetails,
  represented,
  bankruptcy,
  paymentPlan,
  isAgent,
  isCreditRecalled,
  isClosed,
  isReturned,
  accountHasMainPlan,
  hasRewardsProgram,
  isPaid,
}: IGetAccountCardLabel): AccountCardLabel => {
  const daysUntilReport = getDaysUntilCreditReport(
    creditReportDetails?.last_report_type,
    creditReportDetails?.next_report_date
  );
  const inRiskOfCreditReport = !!(daysUntilReport && daysUntilReport <= 30);
  const isInterim = paymentPlan?.payment_plan_type === "INTERIM";

  if (accountHasMainPlan) {
    return {label: "Outstanding balance", variant: "inherit", action: "resolve"};
  }
  if (inVerificationRequest) {
    return {label: "Verification requested", variant: "warning", action: "resolve", progressBarColor: Colors.grey._500};
  }
  if (inDispute) {
    return {label: "Dispute open", variant: "warning", action: "resolve", progressBarColor: Colors.grey._500};
  }
  if (isClosed) {
    return {label: "Closed", variant: "error", action: "resolve"};
  }
  if (isCreditRecalled) {
    return {label: "Recalled by creditor", variant: "error", action: "resolve"};
  }
  if (isReturned) {
    return {label: "Returned to creditor", variant: "error", action: "resolve"};
  }
  if (inRiskOfCreditReport) {
    return {label: "Credit report risk", variant: "error", action: "resolve"};
  }
  if (bankruptcy && !paymentPlan && isAgent) {
    return {label: "Bankruptcy", variant: "error", action: "resolve"};
  }
  if (represented && !paymentPlan && isAgent) {
    return {label: "Represented", variant: "info", action: "resolve"};
  }
  if (paymentPlan?.state === "revoked") {
    return {label: "plan canceled", variant: "error", action: "resolve", progressBarColor: Colors.grey._500};
  }
  if (!paymentPlan || (isInterim && paymentPlan.state === "resolved")) {
    if (isPaid) {
      return {
        label: "Resolved",
        variant: "success",
        action: hasRewardsProgram ? "manage" : undefined,
        progressBarColor: Colors.green._900,
      };
    }
    return {
      label: "No plan yet",
      variant: "inherit",
      action: "resolve",
    };
  }
  if (paymentPlan.late_installment) {
    return {label: "Payment failed", variant: "error", action: "resolve", progressBarColor: Colors.grey._500};
  }
  if (paymentPlan.state === "resolved") {
    return {
      label: "Resolved",
      variant: "success",
      action: hasRewardsProgram ? "manage" : undefined,
      progressBarColor: Colors.green._900,
    };
  }
  if (isInterim) {
    return {label: "Temporary plan", variant: "success", action: "manage", progressBarColor: Colors.green._900};
  }
  return {
    label: "Active plan",
    variant: "success",
    action: "manage",
    progressBarColor: Colors.green._900,
  };
};

export const getBundleDetails = (item: AccountSwitcherItem) =>
  item.charge_off_type === ChargeOffType.POST_CHARGE_OFF ? item.bundle_details : undefined;

export const ELIGIBLE_BUNDLE_KEY = "eligible";

export const getBundleKey = (bundle: BundleDetails | undefined): number | string =>
  bundle?.bundle_payment_plan_id ?? ELIGIBLE_BUNDLE_KEY;

export const isBundleMember = (item: AccountSwitcherItem) => {
  const bundle = getBundleDetails(item);
  return !!bundle?.bundle_eligible || !!bundle?.bundle_active_payment_plan;
};

const AccountOrderRank = {
  NO_ACTIVE_PLAN: 0,
  LATE_PLAN: 1,
  DISPUTED: 2,
  ACTIVE_PLAN: 3,
  RESOLVED_PLAN: 4,
} as const;

const getAccountOrderRank = (item: AccountSwitcherItem): number => {
  if (item.active_dispute) return AccountOrderRank.DISPUTED;
  const planState = item.active_payment_plan?.state;
  if (!planState || planState === "revoked") return AccountOrderRank.NO_ACTIVE_PLAN;
  if (planState === "late") return AccountOrderRank.LATE_PLAN;
  if (planState === "resolved") return AccountOrderRank.RESOLVED_PLAN;
  return AccountOrderRank.ACTIVE_PLAN;
};

const getAccountOrderBalance = (item: AccountSwitcherItem): number =>
  getBundleDetails(item)?.total_bundle_balance ?? item.total_balance;

export const sortAccountSwitcherCards = <T extends AccountSwitcherItem>(accountSwitcher: T[] | undefined): T[] => {
  if (!accountSwitcher) {
    return [];
  }
  return orderBy(accountSwitcher, [getAccountOrderRank, getAccountOrderBalance], ["asc", "desc"]);
};

export type {AccountCardLabel, AccountCardLabelText, IGetAccountCardLabel};
